export const AFFILIATE_REF_STORAGE_KEY = "dople_ref_v1";
export const AFFILIATE_REF_COOKIE = "dople_ref";
export const AFFILIATE_REF_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;
export const AFFILIATE_REF_MAX_AGE_MS = AFFILIATE_REF_MAX_AGE_SECONDS * 1000;

const CODE_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;

export function normalizeAffiliateCode(value) {
  const code = String(value || "").trim().toLowerCase();
  return CODE_PATTERN.test(code) ? code : "";
}

function defaultStorage() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readCookie(name) {
  if (typeof document === "undefined") return "";
  let raw = null;
  try {
    raw = document.cookie;
  } catch {
    return null;
  }
  if (typeof raw !== "string" || !raw) return "";
  const match = raw
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  if (!match) return "";
  try {
    return decodeURIComponent(match.split("=").slice(1).join("="));
  } catch {
    return "";
  }
}

function cookieDomainAttribute() {
  if (typeof window === "undefined") return "";
  const host = window.location.hostname || "";
  if (host === "dopletech.com" || host.endsWith(".dopletech.com")) {
    return "; Domain=.dopletech.com";
  }
  return "";
}

function writeCookie(code) {
  try {
    if (typeof document === "undefined") return null;
    const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${AFFILIATE_REF_COOKIE}=${encodeURIComponent(code)}; Path=/; Max-Age=${AFFILIATE_REF_MAX_AGE_SECONDS}; SameSite=Lax${cookieDomainAttribute()}${secure}`;
    return true;
  } catch {
    return null;
  }
}

function clearCookie() {
  try {
    if (typeof document === "undefined") return null;
    const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${AFFILIATE_REF_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${cookieDomainAttribute()}${secure}`;
    return true;
  } catch {
    return null;
  }
}

function defaultCookieStore() {
  return {
    read: () => readCookie(AFFILIATE_REF_COOKIE),
    write: (code) => writeCookie(code),
    clear: () => clearCookie(),
  };
}

function readRecord(storage) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(AFFILIATE_REF_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const code = normalizeAffiliateCode(parsed && parsed.code);
    const storedAt = Number(parsed && parsed.storedAt);
    if (!code || !storedAt) return null;
    return { code, storedAt };
  } catch {
    return null;
  }
}

function writeRecord(storage, code, storedAt) {
  if (!storage) return;
  try {
    storage.setItem(AFFILIATE_REF_STORAGE_KEY, JSON.stringify({ code, storedAt }));
  } catch {
    // Storage can be blocked; the cookie still carries the code for this browser.
  }
}

export function clearStoredAffiliateCode(storage = defaultStorage(), cookieStore = defaultCookieStore()) {
  if (storage) {
    try {
      storage.removeItem(AFFILIATE_REF_STORAGE_KEY);
    } catch {
      // Ignore storage failures while clearing an expired code.
    }
  }
  if (cookieStore && typeof cookieStore.clear === "function") cookieStore.clear();
}

export function readStoredAffiliateCode(storage = defaultStorage(), now = Date.now(), cookieStore = defaultCookieStore()) {
  const record = readRecord(storage);
  if (record) {
    if (now - record.storedAt > AFFILIATE_REF_MAX_AGE_MS) {
      clearStoredAffiliateCode(storage, cookieStore);
      return "";
    }
    return record.code;
  }
  const cookieCode = normalizeAffiliateCode(cookieStore && cookieStore.read ? cookieStore.read() : "");
  if (!cookieCode) return "";
  writeRecord(storage, cookieCode, now);
  return cookieCode;
}

export function rememberAffiliateCode(code, storage = defaultStorage(), now = Date.now(), cookieStore = defaultCookieStore()) {
  const existing = readStoredAffiliateCode(storage, now, cookieStore);
  if (existing) return existing;
  const normalized = normalizeAffiliateCode(code);
  if (!normalized) return "";
  writeRecord(storage, normalized, now);
  if (cookieStore && typeof cookieStore.write === "function") cookieStore.write(normalized);
  return normalized;
}

export function affiliateCodeOnLocation(location) {
  const search = (location && location.search) || "";
  const pathname = (location && location.pathname) || "";
  const params = new URLSearchParams(search.charAt(0) === "?" ? search.slice(1) : search);
  const fromQuery = params.get("ref") || "";
  const match = String(pathname).match(/^\/(?:r|c)\/([^/]+)\/?$/);
  let fromPath = "";
  if (match) {
    try {
      fromPath = decodeURIComponent(match[1]);
    } catch {
      fromPath = match[1];
    }
  }
  return normalizeAffiliateCode(fromQuery || fromPath);
}

export function captureAffiliateRefFromLocation(location, storage = defaultStorage(), now = Date.now(), cookieStore = defaultCookieStore()) {
  const candidate = affiliateCodeOnLocation(location);
  if (!candidate) return readStoredAffiliateCode(storage, now, cookieStore);
  return rememberAffiliateCode(candidate, storage, now, cookieStore);
}

export function captureAffiliateRefFromWindow() {
  if (typeof window === "undefined") return "";
  return captureAffiliateRefFromLocation(window.location);
}

const CLICK_MEMORY_PREFIX = "dople_aff_click_";
const CLICK_SESSION_KEY = "dople_aff_click_session";
const reportedClicks = new Set();

function clickStorage() {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function anonymousIdForClick() {
  if (typeof window === "undefined") return "";
  try {
    const existing = window.localStorage.getItem("dople_marketing_anonymous_id_v1");
    if (existing) return existing;
    const created = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem("dople_marketing_anonymous_id_v1", created);
    return created;
  } catch {
    return "";
  }
}

export function reportAffiliateClick(code, location) {
  const normalized = normalizeAffiliateCode(code);
  if (!normalized || typeof window === "undefined") return;
  if (reportedClicks.has(normalized)) return;
  reportedClicks.add(normalized);
  const storage = clickStorage();
  const memoryKey = `${CLICK_MEMORY_PREFIX}${normalized}`;
  if (storage && storage.getItem(memoryKey)) return;
  if (storage) {
    try {
      storage.setItem(memoryKey, "1");
    } catch {
      // A blocked session store still allows one in-page report.
    }
  }
  let sessionToken = "";
  if (storage) {
    sessionToken = storage.getItem(CLICK_SESSION_KEY) || "";
    if (!sessionToken) {
      sessionToken = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      try {
        storage.setItem(CLICK_SESSION_KEY, sessionToken);
      } catch {
        sessionToken = "";
      }
    }
  }
  const apiOrigin = process.env.REACT_APP_API_URL || "https://api.dopletech.com";
  const path = (location && location.pathname) || window.location.pathname || "";
  let referrerHost = "";
  try {
    if (typeof document !== "undefined" && document.referrer) {
      referrerHost = new URL(document.referrer).hostname || "";
    }
  } catch {
    referrerHost = "";
  }
  const anonymousId = anonymousIdForClick();
  const body = JSON.stringify({
    code: normalized,
    anonymous_id: anonymousId,
    path: String(path).slice(0, 500),
    referrer_host: referrerHost,
    idempotency_key: [anonymousId || "anon", normalized, sessionToken || "once"].join(":").slice(0, 180),
  });
  fetch(`${apiOrigin}/api/affiliates/clicks`, {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers: { "Content-Type": "application/json" },
    body,
  }).catch(() => {});
}
